import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';

export interface ICampaignAccountOption {
  accountId: string;
  name: string | null;
  /** LinkedIn login email, when the workspace has a mapping for the account. */
  email?: string | null;
  status: string;
}

interface Props {
  accounts: ICampaignAccountOption[];
  /** Accounts currently in the campaign pool — rendered as checked. */
  selectedIds: string[];
  disabled?: boolean;
  /** Draws the trigger in red — set when the step was blocked on having no account. */
  invalid?: boolean;
  /** Toggling adds the account to this campaign, or removes it from this campaign. */
  onToggle: (accountId: string, checked: boolean) => void;
  onAddNew: () => void;
}

/** Stable per-account tint so the same account keeps the same avatar colour. */
const AVATAR_TINTS = ['#eef3ff', '#e8f7f0', '#fff4e6', '#fdecef', '#eef7fd', '#f3eefd'];
const AVATAR_INKS  = ['#4550d6', '#1d7a54', '#b06a1a', '#c0344c', '#1f6b96', '#6b40c4'];

function tintIndex(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return hash % AVATAR_TINTS.length;
}

export function accountInitials(name?: string | null, email?: string | null): string {
  const source = (name || '').trim() || (email || '').trim();
  if (!source) return 'LI';
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return 'LI';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export const AccountAvatar: React.FC<{ name?: string | null; email?: string | null; size?: number }> = ({
  name, email, size = 28,
}) => {
  const idx = tintIndex(`${name ?? ''}${email ?? ''}` || 'li');
  return (
    <Avatar $size={size} $bg={AVATAR_TINTS[idx]} $color={AVATAR_INKS[idx]} aria-hidden>
      {accountInitials(name, email)}
    </Avatar>
  );
};

const CampaignAccountPicker: React.FC<Props> = ({
  accounts, selectedIds, disabled = false, invalid = false, onToggle, onAddNew,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(a =>
      (a.name ?? '').toLowerCase().includes(q) || (a.email ?? '').toLowerCase().includes(q)
    );
  }, [accounts, query]);

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        type="button"
        disabled={disabled}
        $open={open}
        $invalid={invalid}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-invalid={invalid}
      >
        <TriggerLabel>Select an account</TriggerLabel>
        <Chevron $open={open} aria-hidden>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Chevron>
      </Trigger>

      {open && (
        <Panel>
          <SearchInput
            autoFocus
            placeholder="Search by name"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />

          <OptionList>
            {filtered.length === 0 ? (
              <EmptyState>
                {accounts.length === 0
                  ? 'No LinkedIn accounts in this workspace yet.'
                  : 'No accounts match that search.'}
              </EmptyState>
            ) : filtered.map(account => {
              const checked = selectedIds.includes(account.accountId);
              return (
                <Option key={account.accountId}>
                  <OptionCheckbox
                    type="checkbox"
                    checked={checked}
                    onChange={e => onToggle(account.accountId, e.target.checked)}
                  />
                  <AccountAvatar name={account.name} email={account.email} size={24} />
                  <OptionText>
                    <OptionName>{account.name || 'LinkedIn account'}</OptionName>
                    {account.email && <OptionEmail>{account.email}</OptionEmail>}
                  </OptionText>
                </Option>
              );
            })}
          </OptionList>

          <AddNewBtn
            type="button"
            onClick={() => { setOpen(false); onAddNew(); }}
          >
            <PlusGlyph aria-hidden>+</PlusGlyph>
            Add new account
          </AddNewBtn>
        </Panel>
      )}
    </Wrapper>
  );
};

export default CampaignAccountPicker;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 280px;
`;

const Trigger = styled.button<{ $open: boolean; $invalid: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1.5px solid ${({ $open, $invalid }) => (
    $invalid ? styles.colors.red600 : $open ? styles.colors.primary600 : '#dde3ee'
  )};
  box-shadow: ${({ $invalid }) => ($invalid ? '0 0 0 3px rgba(255,103,96,0.15)' : 'none')};
  background: ${styles.colors.white};
  cursor: pointer;
  &:disabled { background: #f7f9fc; cursor: default; }
`;

const TriggerLabel = styled.span`
  font-family: NunitoSans;
  font-size: 13px;
  color: ${styles.colors.black400};
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  color: #677084;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const Panel = styled.div`
  position: absolute;
  z-index: 100;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  min-width: 240px;
  padding: 8px;
  border-radius: 10px;
  border: 1px solid #e6ebf3;
  background: ${styles.colors.white};
  box-shadow: 0 8px 20px rgba(25, 40, 72, 0.10);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SearchInput = styled.input`
  height: 32px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #e6ebf3;
  font-family: NunitoSans;
  font-size: 12.5px;
  color: ${styles.colors.black600};
  outline: none;
  &::placeholder { color: ${styles.colors.black300}; }
  &:focus { border-color: ${styles.colors.primary600}; }
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 220px;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: #e8eaed transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #e8eaed; border-radius: 2px; }
`;

const Option = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  border-radius: 6px;
  cursor: pointer;
  min-width: 0;
  &:hover { background: #f7f9fc; }
`;

const OptionCheckbox = styled.input`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  accent-color: ${styles.colors.primary600};
  cursor: pointer;
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const OptionName = styled.span`
  font-family: NunitoSans;
  font-size: 12.5px;
  font-weight: 700;
  color: ${styles.colors.black600};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OptionEmail = styled.span`
  font-family: NunitoSans;
  font-size: 11px;
  color: ${styles.colors.black300};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.span`
  padding: 10px 4px;
  font-family: NunitoSans;
  font-size: 12px;
  color: ${styles.colors.black300};
`;

const AddNewBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 7px 8px;
  border-radius: 6px;
  border: 1px solid #e6ebf3;
  background: ${styles.colors.white};
  font-family: NunitoSans;
  font-size: 12.5px;
  font-weight: 700;
  color: ${styles.colors.black600};
  cursor: pointer;
  &:hover { background: #f8faff; border-color: #d6deec; }
`;

const PlusGlyph = styled.span`
  font-size: 15px;
  line-height: 1;
  color: ${styles.colors.black500};
`;

const Avatar = styled.span<{ $size: number; $bg: string; $color: string }>`
  width: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-family: NunitoSans;
  font-size: ${({ $size }) => Math.round($size * 0.38)}px;
  font-weight: 800;
  letter-spacing: 0.02em;
`;
